import React, { useMemo, useState } from "react";
import { Box, Paper, Typography, Slider, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { LuLeaf, LuCoins } from "react-icons/lu";
import { TbBrandCarbon } from "react-icons/tb";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { getOrgTokens } from "../../../theme/style/organizationPalette";
import { TableHeaderContainer } from "../../../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../../../components/container/TableTitleContainer";
import MetricCard from "../../../app_components/MetricCard";

const PROJECT_SUMMARY = {
  projectMci: 42.34,
  totalGwp: 79572.63, // kg CO2e
  totalCost: 21100, // CAD
};

const LEVERS = [
  { key: "recycledBoost", label: "Recycled content boost", default: 15 },
  { key: "lifeExt", label: "Product life extension", default: 20 },
  { key: "renewableBoost", label: "Renewable inflow boost", default: 10 },
];

const ScenarioPanel = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  const [values, setValues] = useState(
    LEVERS.reduce((acc, lever) => ({ ...acc, [lever.key]: lever.default }), {})
  );

  const modified = useMemo(() => {
    const { recycledBoost, lifeExt, renewableBoost } = values;
    const mciGain = (recycledBoost * 0.35 + lifeExt * 0.25 + renewableBoost * 0.15) / 10;
    const gwpDropPct = recycledBoost * 0.9 + renewableBoost * 0.6;
    const costDeltaPct = recycledBoost * -0.2 + lifeExt * 0.3;
    return {
      mci: Math.min(95, PROJECT_SUMMARY.projectMci + mciGain),
      gwp: Math.max(0, PROJECT_SUMMARY.totalGwp * (1 - gwpDropPct / 100)),
      cost: PROJECT_SUMMARY.totalCost * (1 + costDeltaPct / 100),
    };
  }, [values]);

  const chart = [
    { name: t("MCI (%)"), Current: PROJECT_SUMMARY.projectMci, Modified: modified.mci },
    { name: t("GWP (t CO₂e)"), Current: PROJECT_SUMMARY.totalGwp / 1000, Modified: modified.gwp / 1000 },
    { name: t("Cost (k CAD)"), Current: PROJECT_SUMMARY.totalCost / 1000, Modified: modified.cost / 1000 },
  ];

  const mciDiff = modified.mci - PROJECT_SUMMARY.projectMci;
  const gwpDiffT = (modified.gwp - PROJECT_SUMMARY.totalGwp) / 1000;
  const costDiff = modified.cost - PROJECT_SUMMARY.totalCost;

  const deltaSubtitle = (diff, unit, decimals = 2) =>
    `${diff > 0 ? "+" : ""}${diff.toFixed(decimals)} ${unit} ${t("vs current")}`;

  const statCards = [
    {
      title: "Project MCI",
      subtitle: deltaSubtitle(mciDiff, "%"),
      value: modified.mci,
      unit: "%",
      icon: LuLeaf,
      color: mciDiff > 0 ? "#16a34a" : mciDiff < 0 ? "#ef4444" : "#64748b",
      bg: mciDiff > 0 ? "rgba(22, 163, 74, 0.1)" : mciDiff < 0 ? "rgba(239, 68, 68, 0.1)" : "rgba(100, 116, 139, 0.1)",
    },
    {
      title: "Total GWP",
      subtitle: deltaSubtitle(gwpDiffT, "t CO₂e"),
      value: modified.gwp,
      unit: "kgCO2e",
      icon: TbBrandCarbon,
      color: gwpDiffT < 0 ? "#16a34a" : gwpDiffT > 0 ? "#ef4444" : "#64748b",
      bg: gwpDiffT < 0 ? "rgba(22, 163, 74, 0.1)" : gwpDiffT > 0 ? "rgba(239, 68, 68, 0.1)" : "rgba(100, 116, 139, 0.1)",
    },
    {
      title: "Total Cost",
      subtitle: deltaSubtitle(costDiff, "CAD", 0),
      value: modified.cost,
      unit: "CAD",
      icon: LuCoins,
      color: costDiff < 0 ? "#16a34a" : costDiff > 0 ? "#ef4444" : "#64748b",
      bg: costDiff < 0 ? "rgba(22, 163, 74, 0.1)" : costDiff > 0 ? "rgba(239, 68, 68, 0.1)" : "rgba(100, 116, 139, 0.1)",
    },
  ];

  return (
    <Box sx={{ display: "grid", gap: 3 }}>
      <Paper sx={{ border: 1, borderColor: tok.border.card, borderRadius: tok.radius.card, overflow: "hidden" }}>
        <TableHeaderContainer>
          <TableTitleContainer>
            <Typography variant="Table Title">{t("Design levers")}</Typography>
          </TableTitleContainer>
        </TableHeaderContainer>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" } }}>
            {LEVERS.map((lever) => (
              <Box key={lever.key}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography sx={{ fontSize: tok.font.size.bodyText, fontWeight: 500, color: tok.text.primary, fontFamily: tok.font.family }}>
                    {t(lever.label)}
                  </Typography>
                  <Typography sx={{ fontSize: tok.font.size.bodyText, fontWeight: 600, color: "#659d33", fontFamily: tok.font.family }}>
                    +{values[lever.key]}%
                  </Typography>
                </Box>
                <Slider
                  value={values[lever.key]}
                  min={0}
                  max={50}
                  step={1}
                  onChange={(e, v) => setValues((prev) => ({ ...prev, [lever.key]: v }))}
                  sx={{
                    color: "#659d33",
                    "& .MuiSlider-thumb": { backgroundColor: "#659d33" },
                    "& .MuiSlider-track": { backgroundColor: "#659d33" },
                    "& .MuiSlider-rail": { backgroundColor: isDark ? "#3f3f46" : "#e2e8f0" },
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(auto-fit, minmax(220px, 1fr))", lg: "repeat(3, 1fr)" }, gap: 2.5 }}>
        {statCards.map((c, i) => (
          <MetricCard
            key={i}
            title={t(c.title)}
            subtitle={c.subtitle}
            value={c.value}
            unit={c.unit}
            icon={c.icon}
            accent={c.color}
            bg={c.bg}
            delay={i * 0.1}
          />
        ))}
      </Box>

      <Paper sx={{ border: 1, borderColor: tok.border.card, borderRadius: tok.radius.card, overflow: "hidden" }}>
        <TableHeaderContainer>
          <TableTitleContainer>
            <Typography variant="Table Title">{t("Current vs Modified")}</Typography>
          </TableTitleContainer>
        </TableHeaderContainer>
        <Box sx={{ p: 3 }}>
          <Box sx={{ height: 288 }}>
            <ResponsiveContainer>
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke={tok.border.card} opacity={0.6} />
                <XAxis dataKey="name" fontSize={12} stroke={tok.text.muted} />
                <YAxis fontSize={12} stroke={tok.text.muted} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: tok.bg.card,
                    border: `1px solid ${tok.border.card}`,
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontFamily: tok.font.family,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: "12px", fontFamily: tok.font.family }} />
                <Bar dataKey="Current" name={t("Current")} fill={isDark ? "#71717a" : "#94a3b8"} radius={[4, 4, 0, 0]} />
                <Bar dataKey="Modified" name={t("Modified")} fill="#659d33" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ScenarioPanel;
